/**
 * Structured error classification for the Response Envelope (BET-78, FEAT-121).
 *
 * Typed error classes + classifyError pattern-matching.
 * classifyError handles both typed classes (new code) and plain Error
 * messages from client.ts gates (existing code — no modifications needed).
 *
 * BET-191 (FEAT-573): KernelCallError carries a .code from the Convex HTTP gateway
 * (classifyMcpGatewayError). VALIDATION_FAILED errors must surface the clean
 * validation message — not the HTTP wrapper prefix — so agents receive actionable
 * "Field X must be one of: ..." text without noise.
 *
 * WP-321 E3a: Output codes emitted by classifyError are registered as the unified
 * error code SSOT in convex/lib/errors.ts::ErrorCodes. The MCP server cannot
 * import from convex/ (monorepo boundary: ARCH-55, BR-154), so the string values
 * are maintained as inline constants here — but they MUST match the corresponding
 * entries in ErrorCodes exactly. Verified output codes:
 *   SESSION_REQUIRED, SESSION_CLOSED, ORIENTATION_REQUIRED, READONLY_SCOPE,
 *   BACKEND_UNAVAILABLE, BACKEND_ERROR, INTERNAL_ERROR, DUPLICATE,
 *   PERMISSION_DENIED, VALIDATION_ERROR, NOT_FOUND
 * The canonical registry lives in convex/lib/errors.ts — update there first
 * if a code name ever needs to change, then update the strings here to match.
 */

import type { NextAction } from "./envelope.js";

// ─── KernelCallError duck-type helpers ──────────────────────────────────────────
// KernelCallError is defined in client.ts and carries the structured code from the
// Convex HTTP gateway. We use duck typing to avoid a circular import.

/**
 * Matches `MCP call "fn" failed (STATUS): <clean message>` — the KernelCallError
 * message format from client.ts. Group 1 = fn scope, group 2 = clean backend message.
 */
// DEC-798: group 1 = fn scope, group 2 = clean message
const MCP_CALL_ERROR_RE = /^MCP call "([^"]+)" failed \(\d+\): (.+)$/s;

/**
 * If err is a KernelCallError (duck typed via .code and MCP_CALL_ERROR_RE message),
 * extract the structured code, fn scope, clean message, and WP-316 S1a diagnostics.
 * Returns undefined if not a match.
 */
function asKernelCallError(
  err: unknown,
): {
  code: string;
  fn: string;
  cleanMessage: string;
  missingRequiredFields?: string[];
  fieldErrors?: string[];
} | undefined {
  if (!(err instanceof Error)) return undefined;
  const e = err as Error & {
    code?: unknown;
    missingRequiredFields?: unknown;
    fieldErrors?: unknown;
  };
  if (typeof e.code !== "string") return undefined;
  const match = MCP_CALL_ERROR_RE.exec(e.message);
  if (!match) return undefined;
  return {
    code: e.code,
    fn: match[1],
    cleanMessage: match[2],   // NOTE: match[2] not match[1] — group 1 is now fn scope (DEC-798)
    missingRequiredFields: Array.isArray(e.missingRequiredFields) ? e.missingRequiredFields : undefined,
    fieldErrors: Array.isArray(e.fieldErrors) ? e.fieldErrors : undefined,
  };
}

// ─── Typed Error Classes ─────────────────────────────────────────────────

export class GateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GateError";
  }
}

export class BackendError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BackendError";
  }
}

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ValidationError";
  }
}

// ─── Classification Result ───────────────────────────────────────────────

export interface ClassifiedError {
  code: string;
  message: string;
  fn?: string;  // DEC-798: Convex function scope from KernelCallError
  recovery?: string;
  availableActions?: NextAction[];
  /** WP-316 S1a: Structured commit validation diagnostics when code=VALIDATION_ERROR. */
  diagnostics?: {
    missingRequiredFields?: string[];
    fieldErrors?: string[];
  };
}

// ─── Gate Patterns (from client.ts requireWriteAccess / requireActiveSession) ─

interface GatePattern {
  pattern: RegExp;
  code: string;
  recovery: string;
  action: NextAction;
}

const GATE_PATTERNS: GatePattern[] = [
  {
    pattern: /session required|no active.*session|call.*session.*start/i,
    code: "SESSION_REQUIRED",
    recovery: "Start an agent session first.",
    action: { tool: "session", description: "Start session", parameters: { action: "start" } },
  },
  {
    pattern: /session.*closed/i,
    code: "SESSION_CLOSED",
    recovery: "Start a new session.",
    action: { tool: "session", description: "Start new session", parameters: { action: "start" } },
  },
  {
    pattern: /orientation required|call.*orient/i,
    code: "ORIENTATION_REQUIRED",
    recovery: "Orient before writing.",
    action: { tool: "orient", description: "Orient session", parameters: {} },
  },
  {
    pattern: /read.?only.*scope/i,
    code: "READONLY_SCOPE",
    recovery: "This API key cannot write. Use a readwrite key.",
    action: { tool: "workspace", description: "Check key scope", parameters: { action: "whoami" } },
  },
];

// ─── Classification ──────────────────────────────────────────────────────

/**
 * Classify an error into a structured code + recovery path.
 * Handles:
 *   1. Gate errors (from requireWriteAccess / requireActiveSession)
 *   2. KernelCallError with structured Convex codes (VALIDATION_FAILED, NOT_FOUND, DUPLICATE)
 *   3. Backend errors (network/connectivity)
 *   4. Pattern-matched validation / not-found / duplicate messages
 *   5. Fallback to INTERNAL_ERROR
 */
export function classifyError(err: unknown): ClassifiedError {
  const message = err instanceof Error ? err.message : String(err);

  if (err instanceof GateError) {
    return {
      code: "PERMISSION_DENIED",
      message,
      recovery: "Check permissions or use a readwrite API key.",
      availableActions: [{ tool: "workspace", description: "Check key scope", parameters: { action: "whoami" } }],
    };
  }

  if (err instanceof BackendError) {
    return { code: "BACKEND_ERROR", message, recovery: "Retry the operation or check backend health." };
  }

  if (err instanceof ValidationError) {
    return { code: "VALIDATION_ERROR", message };
  }

  // BET-191 (FEAT-573): KernelCallError carries the structured Convex error code from
  // classifyMcpGatewayError. Use it for clean classification before falling through
  // to regex matching, so agents receive the clean backend message without the
  // "MCP call ... failed (400):" HTTP wrapper prefix.
  const mcpErr = asKernelCallError(err);
  if (mcpErr) {
    const { code: convexCode, fn, cleanMessage, missingRequiredFields, fieldErrors } = mcpErr;
    if (convexCode === "VALIDATION_FAILED") {
      const hasDiagnostics = missingRequiredFields?.length || fieldErrors?.length;
      return {
        code: "VALIDATION_ERROR",
        fn,
        message: cleanMessage,
        ...(hasDiagnostics ? {
          diagnostics: {
            ...(missingRequiredFields?.length ? { missingRequiredFields } : {}),
            ...(fieldErrors?.length ? { fieldErrors } : {}),
          },
        } : {}),
      };
    }
    if (convexCode === "NOT_FOUND") {
      return {
        code: "NOT_FOUND",
        fn,
        message: cleanMessage,
        recovery: "Use entries action=search to find the correct ID.",
        availableActions: [{ tool: "entries", description: "Search entries", parameters: { action: "search" } }],
      };
    }
    if (convexCode === "WORKSPACE_SLUG_TAKEN" || convexCode === "PERSON_ALREADY_EXISTS") {
      return {
        code: "DUPLICATE",
        fn,
        message: cleanMessage,
        recovery: "Use entries action=get to inspect the existing entry.",
        availableActions: [{ tool: "entries", description: "Get entry", parameters: { action: "get" } }],
      };
    }
    // TEN-656: per-key API quota (HTTP 429, code 'rate-limited' — registered in
    // convex/lib/errors.ts ErrorCodes.RATE_LIMITED). The refusal must be agent-legible
    // (TEN-2655): say exactly what to do — wait, then retry the same call unchanged.
    if (convexCode === "rate-limited") {
      return {
        code: "rate-limited",
        fn,
        message: cleanMessage,
        recovery:
          "Rate limit hit for this API key. Wait the number of seconds in the message (Retry-After), then retry the same call unchanged — it is safe to retry.",
      };
    }
    // Fallthrough for any other convexCode: preserve fn for the prefix format
    return { code: convexCode, fn, message: cleanMessage };
  }

  for (const { pattern, code, recovery, action } of GATE_PATTERNS) {
    if (pattern.test(message)) {
      return { code, message, recovery, availableActions: [action] };
    }
  }

  if (/network error|fetch failed|ECONNREFUSED|ETIMEDOUT/i.test(message)) {
    return { code: "BACKEND_UNAVAILABLE", message, recovery: "Retry in a few seconds." };
  }

  return { code: "INTERNAL_ERROR", message: message || "An unexpected error occurred." };
}
